import React from 'react';
import { Animated } from 'react-native';
import {
  KeyboardAvoidingView, Center, Spinner, Box,
} from '@gluestack-ui/themed';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { useTenantSettingsStore } from '../../common/context/TenantSettingsStore';
import MaintenanceMessage from '../../common/components/MaintenanceMessage';
import { AnimatedBackground } from './components/AnimatedBackground';
import { LoginForm } from './components/LoginForm';
import { useLoginLogic } from './hooks/useLoginLogic';

/**
 * Login screen orchestrator.
 * Handles tenant-level states (loading, maintenance) and composes the login UI.
 *
 * @returns Fully composed Login screen
 */
const LoginScreen = () => {
  const navigation = useNavigation<NavigationProp<Record<string, unknown>>>();
  const logic = useLoginLogic();

  const { settings, status, isInMaintenance } = useTenantSettingsStore((state) => ({
    settings: state.settings,
    status: state.status,
    isInMaintenance: state.isInMaintenance,
  }));

  const handleForgotPassword = () => navigation.navigate('ForgotPassword');

  // Loading state for tenant settings
  if (status === 'loading') {
    return (
      <Center w="100%" h="100%" bg="$bgApp">
        <Spinner testID="spinner" size="large" />
      </Center>
    );
  }

  // Maintenance or error state
  if (status === 'error' || isInMaintenance()) {
    return (
      <Center w="100%" h="100%" bg="$bgApp" p="$4">
        <MaintenanceMessage maintenanceState={settings?.maintenanceState} />
      </Center>
    );
  }

  return (
    <KeyboardAvoidingView w="100%" h="100%" alignItems="center" justifyContent="center" behavior="height" bg="$bgApp">
      <AnimatedBackground />

      <Box w="100%" maxWidth={480} px="$4">
        <Animated.View
          style={{
            opacity: logic.fadeAnim,
            transform: [{ translateY: logic.slideAnim }],
          }}
        >
          <LoginForm
            logic={logic}
            displayName={settings?.naming?.displayName}
            logoUrl={settings?.imageAssetUrls?.logoLargeUrl}
            onForgotPassword={handleForgotPassword}
          />
        </Animated.View>
      </Box>
    </KeyboardAvoidingView>
  );
};

export default LoginScreen;
